import React from 'react';
import styled from '@emotion/styled';
import type { ColorProps } from 'styled-system';
import { color } from 'styled-system';

import { useLocalisedDateRange } from '../../hooks/date';
import { useLocalization } from '../../providers/LocaleProvider';
import { HeadlineH7 } from '../Typography';

export const enum DeliveryDateRangeType {
  Default,
  Mini,
  MiniStrong,
  Strong,
  Plain,
}

interface DeliveryDateRangeProps {
  type?: DeliveryDateRangeType;
  possibleDeliveryDateMin?: string;
  possibleDeliveryDateMax?: string;
  overFreeShippingThreshold?: boolean;
  virtualDeliveryLabel?: string;
  className?: string;
}

interface VirtualDeliveryDateRangeProps {
  type?: DeliveryDateRangeType;
  virtualDeliveryLabel: string;
  className?: string;
}

interface PhysicalDeliveryDateRangeProps {
  type?: DeliveryDateRangeType;
  possibleDeliveryDateMin: string;
  possibleDeliveryDateMax: string;
  overFreeShippingThreshold?: boolean;
  className?: string;
}

const ContainerBase = styled.span<ColorProps>`
  strong {
    display: inline-block;
  }
  ${color};
`;

const getLabel = (type: DeliveryDateRangeType, overFreeShippingThreshold?: boolean) => {
  if (overFreeShippingThreshold) {
    return 'h24_free_delivery_date_circa';
  }

  return type === DeliveryDateRangeType.Mini || type === DeliveryDateRangeType.MiniStrong
    ? 'h24_date_circa'
    : 'h24_delivery_date_circa';
};

const VirtualDelivery = ({
  type = DeliveryDateRangeType.Default,
  virtualDeliveryLabel,
  className,
}: VirtualDeliveryDateRangeProps) => {
  const { t } = useLocalization();

  const Container = type === DeliveryDateRangeType.Strong ? HeadlineH7 : ContainerBase;

  return (
    <Container
      {...(type === DeliveryDateRangeType.Plain ? { color: 'sys.neutral.text.default' } : {})}
      dangerouslySetInnerHTML={{ __html: t(virtualDeliveryLabel) }}
      className={className}
    />
  );
};

const PhysicalDelivery = ({
  type = DeliveryDateRangeType.Default,
  possibleDeliveryDateMin,
  possibleDeliveryDateMax,
  overFreeShippingThreshold,
  className,
}: PhysicalDeliveryDateRangeProps) => {
  const { r } = useLocalization();
  const dateRange = useLocalisedDateRange({
    minDate: possibleDeliveryDateMin,
    maxDate: possibleDeliveryDateMax,
  });

  const Container = type === DeliveryDateRangeType.Strong ? HeadlineH7 : ContainerBase;
  const label = getLabel(type, overFreeShippingThreshold);
  const date = [DeliveryDateRangeType.Mini, DeliveryDateRangeType.Plain].includes(type)
    ? dateRange
    : `<strong>${dateRange}</strong>`;

  return (
    <Container
      {...(type === DeliveryDateRangeType.Plain ? { color: 'sys.neutral.text.default' } : {})}
      dangerouslySetInnerHTML={{ __html: r.t(label, { date }) }}
      className={className}
    />
  );
};

const DeliveryDateRange = ({
  type = DeliveryDateRangeType.Default,
  possibleDeliveryDateMin,
  possibleDeliveryDateMax,
  overFreeShippingThreshold,
  virtualDeliveryLabel,
  className,
}: DeliveryDateRangeProps) => {
  if (virtualDeliveryLabel) {
    return (
      <VirtualDelivery
        type={type}
        virtualDeliveryLabel={virtualDeliveryLabel}
        className={className}
      />
    );
  }

  if (possibleDeliveryDateMin && possibleDeliveryDateMax) {
    return (
      <PhysicalDelivery
        type={type}
        possibleDeliveryDateMin={possibleDeliveryDateMin}
        possibleDeliveryDateMax={possibleDeliveryDateMax}
        overFreeShippingThreshold={overFreeShippingThreshold}
        className={className}
      />
    );
  }

  return null;
};

export default DeliveryDateRange;
